import {
  useCallback, useEffect, useId, useLayoutEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Clock } from 'lucide-react';
import { BottomSheet, Button, useResponsive } from '@parsina/framework';
import { C } from '../../constants/theme';
import { useTranslation, toAsciiDigits } from '../../i18n';

/**
 * TimePicker — a clock time field, `HH:MM` on the wire.
 *
 * Replaces the native `<input type="time">`, whose popup is whatever the OS
 * draws: an English AM/PM spinner inside a Persian panel on Windows, a
 * browser-grey dropdown that ignores every token, and digits that never follow
 * the locale. The kit has no time field at the pinned tag, so this sits at the
 * app-shared layer (DESIGN.md sourcing rule) — composed from kit pieces and the
 * system's overlay roles, and the thing to promote once a second app needs it.
 *
 * Three ways in, one value:
 *   • TYPE — the field is a real text input. Digits from any shipped keyboard
 *     (`toAsciiDigits`), `830` → 08:30 and `14` → 14:00 on blur/Enter, and a
 *     complete `HH:MM` is committed as it is typed, so the caller's dirty flag
 *     moves without waiting for a blur.
 *   • PICK — clicking the field (or ↓ / Alt+↓) opens two scroll columns, hours
 *     and minutes, with an AM/PM column when `hourCycle={12}`. Every pick
 *     applies at once; picking a minute closes the panel, because hour-then-
 *     minute is the whole gesture.
 *   • SUGGEST — optional one-tap chips for the times a screen actually uses.
 *
 * Surfaces (DESIGN_SYSTEM §5 roles): desktop is an anchored popover portaled to
 * `<body>` at the `Menu` layer (z 900 — above a Drawer, below a ConfirmDialog);
 * ≤ 768px is a `BottomSheet` with larger rows and a confirm, because a popover
 * anchored to a field near the bottom of a phone is not usable.
 *
 * 24-hour is the default everywhere: Iranian clinical scheduling is written in
 * 24-hour time and `formatTime` renders it that way in every locale.
 * `hourCycle={12}` only changes what is SHOWN; the value stays 24-hour ASCII.
 *
 * The clock is an LTR island (§6.4) — hours sit left of minutes in Persian too —
 * while its digits follow the locale through `formatDigits`.
 */

const ROW = 36;
const ROW_MOBILE = 44;
const VISIBLE_ROWS = 5;
const Z_POPOVER = 900;
const EDGE = 8;

const pad = (n) => String(n).padStart(2, '0');

/** A complete, valid `HH:MM` (ASCII) or null. */
export function strictTime(text) {
  const m = /^(\d{2}):(\d{2})$/.exec(String(text || ''));
  if (!m) return null;
  const h = Number(m[1]); const min = Number(m[2]);
  return h <= 23 && min <= 59 ? `${m[1]}:${m[2]}` : null;
}

/**
 * Lenient parse of what someone typed — the blur/Enter path.
 * `8` → 08:00, `830` → 08:30, `0830` → 08:30, `8:5` → 08:05, `14` → 14:00.
 * Any shipped locale's digits are accepted. Returns null when it is not a time.
 */
export function parseTimeText(text) {
  const ascii = toAsciiDigits(text).trim().replace(/[.٫،,]/g, ':');
  if (!ascii) return null;
  let h; let m;
  if (ascii.includes(':')) {
    const [a, b = '0'] = ascii.split(':');
    if (!/^\d{1,2}$/.test(a) || !/^\d{0,2}$/.test(b)) return null;
    h = Number(a); m = Number(b || 0);
  } else {
    if (!/^\d{1,4}$/.test(ascii)) return null;
    if (ascii.length <= 2) { h = Number(ascii); m = 0; } else {
      h = Number(ascii.slice(0, ascii.length - 2)); m = Number(ascii.slice(-2));
    }
  }
  return h <= 23 && m <= 59 ? `${pad(h)}:${pad(m)}` : null;
}

const splitTime = (value) => {
  const v = strictTime(value);
  return v ? { h: Number(v.slice(0, 2)), m: Number(v.slice(3)) } : null;
};

export default function TimePicker({
  value,
  onChange,
  label,
  disabled = false,
  invalid = false,
  describedBy,
  hourCycle = 24,
  minuteStep = 5,
  suggestions = [],
  width = '100%',
  maxWidth = 140,
}) {
  const { t, formatDigits } = useTranslation();
  const { isMobile } = useResponsive();
  const panelId = useId();

  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');

  // A pick made while the field has focus must show in the field, but a
  // partial "1" being typed must not be wiped by an unrelated re-render — so
  // the draft only follows `value` when `value` actually changes.
  useEffect(() => {
    setDraft((d) => (strictTime(toAsciiDigits(d)) === value ? d : formatDigits(value || '')));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = useCallback((next) => {
    if (next && next !== value) onChange?.(next);
  }, [value, onChange]);

  const close = useCallback((refocus = false) => {
    setOpen(false);
    if (refocus) inputRef.current?.focus({ preventScroll: true });
  }, []);

  const openPanel = () => { if (!disabled) setOpen(true); };

  // ── Typing ────────────────────────────────────────────────────────────────
  const handleText = (e) => {
    let ascii = toAsciiDigits(e.target.value).replace(/[^\d:]/g, '');
    if (!ascii.includes(':') && ascii.length === 4) ascii = `${ascii.slice(0, 2)}:${ascii.slice(2)}`;
    ascii = ascii.slice(0, 5);
    setDraft(formatDigits(ascii));
    const complete = strictTime(ascii);
    if (complete) commit(complete);
  };

  const settleDraft = () => {
    const parsed = parseTimeText(draft);
    if (parsed) { commit(parsed); setDraft(formatDigits(parsed)); } else setDraft(formatDigits(value || ''));
  };

  const handleKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      requestAnimationFrame(() => panelRef.current?.querySelector('[role="listbox"]')?.focus());
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation();
      close();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      settleDraft();
      close();
    }
  };

  // Close when focus leaves both the field and the panel (Tab onward).
  const handleBlurWithin = (e) => {
    const next = e.relatedTarget;
    if (next && (wrapRef.current?.contains(next) || panelRef.current?.contains(next))) return;
    setFocused(false);
    settleDraft();
    if (!isMobile) setOpen(false);
  };

  const display = focused ? draft : formatDigits(value || '');

  return (
    <div ref={wrapRef} style={{ position: 'relative', width, maxWidth, minWidth: 0 }} onBlur={handleBlurWithin}>
      <input
        ref={inputRef}
        type="text"
        dir="ltr"
        inputMode={isMobile ? 'none' : 'numeric'}
        readOnly={isMobile}
        autoComplete="off"
        role="combobox"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        placeholder={t('common.time.picker.placeholder')}
        value={display}
        disabled={disabled}
        onFocus={(e) => { setFocused(true); setDraft(formatDigits(value || '')); e.target.select?.(); }}
        onMouseDown={() => { if (!open) openPanel(); }}
        onChange={handleText}
        onKeyDown={handleKey}
        style={{
          height: isMobile ? 44 : 40, width: '100%', boxSizing: 'border-box',
          padding: '0 10px 0 32px',
          border: `1.5px solid ${invalid ? C.red : (open ? C.navy : C.border)}`,
          borderRadius: 10, outline: 'none',
          boxShadow: open && !invalid ? `0 0 0 3px color-mix(in srgb, var(--p-navy) 14%, transparent)` : 'none',
          fontFamily: 'inherit', fontSize: isMobile ? 16 : 13.5, fontVariantNumeric: 'tabular-nums',
          color: C.textPrimary, background: disabled ? C.bg : '#fff',
          cursor: disabled ? 'not-allowed' : 'pointer', textAlign: 'center', unicodeBidi: 'isolate',
          transition: 'border-color .15s, box-shadow .15s',
        }}
      />
      <Clock
        size={15}
        aria-hidden
        style={{
          position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
          color: open ? C.navy : C.textMuted, pointerEvents: 'none',
        }}
      />

      {open && !isMobile && (
        <Popover anchorRef={wrapRef} panelRef={panelRef} onDismiss={() => close()}>
          <TimePanel
            id={panelId}
            label={label}
            value={value}
            hourCycle={hourCycle}
            minuteStep={minuteStep}
            suggestions={suggestions}
            onPick={(next, done) => { commit(next); setDraft(formatDigits(next)); if (done) close(true); }}
            onClose={() => close(true)}
          />
        </Popover>
      )}

      {isMobile && (
        <MobileTimeSheet
          open={open}
          id={panelId}
          panelRef={panelRef}
          label={label}
          value={value}
          hourCycle={hourCycle}
          minuteStep={minuteStep}
          suggestions={suggestions}
          onCancel={() => close()}
          onDone={(next) => { commit(next); close(); }}
        />
      )}
    </div>
  );
}

/** Portaled, flips above the field when there is no room below. */
function Popover({ anchorRef, panelRef, onDismiss, children }) {
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const w = panel?.offsetWidth || 240;
    const h = panel?.offsetHeight || 300;
    const below = window.innerHeight - r.bottom;
    const top = below < h + EDGE && r.top > h + EDGE ? r.top - h - 6 : r.bottom + 6;
    // Aligned to the field's inline START: its right edge under RTL.
    const rtl = document.documentElement.getAttribute('dir') !== 'ltr';
    let left = rtl ? r.right - w : r.left;
    left = Math.max(EDGE, Math.min(left, window.innerWidth - w - EDGE));
    setPos({ top, left });
  }, [anchorRef, panelRef]);

  useLayoutEffect(() => { place(); }, [place]);

  useEffect(() => {
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || anchorRef.current?.contains(e.target)) return;
      onDismiss();
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [anchorRef, panelRef, onDismiss, place]);

  return createPortal(
    <div
      ref={panelRef}
      // Mouse picks keep focus in the field, so typing still works after a click.
      onMouseDown={(e) => { if (!e.target.closest('[role="listbox"]')) e.preventDefault(); }}
      style={{
        position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? -9999, zIndex: Z_POPOVER,
        background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12,
        boxShadow: '0 8px 24px rgba(0,0,0,.12)', padding: 10,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

function MobileTimeSheet({
  open, id, panelRef, label, value, hourCycle, minuteStep, suggestions, onCancel, onDone,
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value || '08:00');
  useEffect(() => { if (open) setDraft(strictTime(value) || '08:00'); }, [open, value]);

  return (
    <BottomSheet
      open={open}
      onClose={onCancel}
      title={label}
      closeLabel={t('common.actions.close')}
      footer={(
        <div style={{ display: 'flex', gap: 8 }}>
          <Button style={{ flex: 1 }} onClick={() => onDone(draft)}>{t('common.actions.confirm')}</Button>
          <Button variant="outline" style={{ flex: 1 }} onClick={onCancel}>{t('common.actions.cancel')}</Button>
        </div>
      )}
    >
      <div ref={panelRef} style={{ display: 'flex', justifyContent: 'center', paddingBlock: 8 }}>
        <TimePanel
          id={id}
          label={label}
          value={draft}
          hourCycle={hourCycle}
          minuteStep={minuteStep}
          suggestions={suggestions}
          rowHeight={ROW_MOBILE}
          onPick={(next) => setDraft(next)}
        />
      </div>
    </BottomSheet>
  );
}

/**
 * The picking surface, shared by both overlays: a readout, the columns, and
 * the suggestion chips. `onPick(next, done)` — `done` is true for a gesture
 * that finishes the choice (a minute, a chip, Enter).
 */
function TimePanel({
  id, label, value, hourCycle, minuteStep, suggestions, rowHeight = ROW, onPick, onClose,
}) {
  const { t, formatDigits } = useTranslation();
  const cur = splitTime(value);
  const h24 = cur?.h ?? null;
  const min = cur?.m ?? null;
  const twelve = hourCycle === 12;
  const pm = h24 != null && h24 >= 12;

  const hours = twelve ? [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] : Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) => i * minuteStep);
  if (min != null && !minutes.includes(min)) { minutes.push(min); minutes.sort((a, b) => a - b); }

  const shownHour = h24 == null ? null : (twelve ? (h24 % 12 || 12) : h24);
  const build = (h, m) => `${pad(h)}:${pad(m)}`;
  const toH24 = (h12, isPm) => (h12 % 12) + (isPm ? 12 : 0);

  const pickHour = (h) => onPick(build(twelve ? toH24(h, pm) : h, min ?? 0), false);
  const pickMinute = (m, done = true) => onPick(build(h24 ?? 8, m), done);
  const pickPeriod = (isPm) => onPick(build(toH24(shownHour ?? 8, isPm), min ?? 0), false);

  const columnsRef = useRef(null);
  const focusColumn = (i) => {
    const cols = columnsRef.current?.querySelectorAll('[role="listbox"]');
    if (cols?.[i]) cols[i].focus();
  };

  const periodLabel = (isPm) => (isPm ? t('common.time.picker.pm') : t('common.time.picker.am'));

  return (
    <div
      id={id}
      role="dialog"
      aria-label={label}
      // Escape closes THIS panel only — the drawer behind it listens too.
      onKeyDown={(e) => { if (e.key === 'Escape' && onClose) { e.preventDefault(); e.stopPropagation(); onClose(); } }}
      style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div
        dir="ltr"
        aria-live="polite"
        style={{
          textAlign: 'center', fontSize: 22, fontWeight: 700, color: value ? C.navy : C.textMuted,
          fontVariantNumeric: 'tabular-nums', letterSpacing: 1, unicodeBidi: 'isolate',
        }}
      >
        {value ? formatDigits(twelve ? `${pad(shownHour)}:${pad(min)}` : value) : t('common.time.picker.placeholder')}
        {value && twelve && <span style={{ fontSize: 13, marginInlineStart: 6 }}>{periodLabel(pm)}</span>}
      </div>

      <div ref={columnsRef} dir="ltr" style={{ display: 'flex', alignItems: 'stretch', gap: 6, justifyContent: 'center' }}>
        <Column
          label={t('common.time.picker.hours')}
          items={hours}
          selected={shownHour}
          rowHeight={rowHeight}
          render={(h) => formatDigits(pad(h))}
          onSelect={(h) => pickHour(h)}
          onEnter={() => focusColumn(1)}
          onNeighbour={(dir) => focusColumn(dir > 0 ? 1 : 0)}
        />
        <div aria-hidden style={{ alignSelf: 'center', fontWeight: 700, color: C.textMuted }}>:</div>
        <Column
          label={t('common.time.picker.minutes')}
          items={minutes}
          selected={min}
          rowHeight={rowHeight}
          render={(m) => formatDigits(pad(m))}
          onSelect={(m, viaKeyboard) => pickMinute(m, !viaKeyboard)}
          onEnter={() => (twelve ? focusColumn(2) : onClose?.())}
          onNeighbour={(dir) => focusColumn(dir > 0 ? (twelve ? 2 : 1) : 0)}
        />
        {twelve && (
          <Column
            label={t('common.time.picker.period')}
            items={[false, true]}
            selected={h24 == null ? null : pm}
            rowHeight={rowHeight}
            render={(isPm) => periodLabel(isPm)}
            onSelect={(isPm) => pickPeriod(isPm)}
            onEnter={() => onClose?.()}
            onNeighbour={(dir) => focusColumn(dir > 0 ? 2 : 1)}
          />
        )}
      </div>

      {suggestions.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
          <span style={{ fontSize: 11.5, color: C.textMuted }}>{t('common.time.picker.suggestions')}</span>
          <div dir="ltr" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxWidth: twelve ? 260 : 200 }}>
            {suggestions.map((s) => {
              const active = s === value;
              return (
                <button
                  key={s}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onPick(s, true)}
                  style={{
                    height: 30, padding: '0 10px', borderRadius: 999, cursor: 'pointer',
                    border: `1px solid ${active ? C.navy : C.border}`,
                    background: active ? 'color-mix(in srgb, var(--p-navy) 8%, transparent)' : '#fff',
                    color: active ? C.navy : C.textSecondary,
                    fontFamily: 'inherit', fontSize: 12.5, fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatDigits(s)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * One scrolling column. A `listbox` with `aria-activedescendant`, so the column
 * is a single Tab stop and ↑/↓/Home/End/PageUp/PageDown move the selection as a
 * native spinner would; ←/→ step to the neighbouring column, Enter moves on.
 */
function Column({
  label, items, selected, rowHeight, render, onSelect, onEnter, onNeighbour,
}) {
  const baseId = useId();
  const listRef = useRef(null);
  const index = items.findIndex((v) => v === selected);

  // Centre the selection — instantly on open, smoothly as it changes.
  const first = useRef(true);
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el || index < 0) return;
    const top = index * rowHeight - (el.clientHeight - rowHeight) / 2;
    if (first.current || typeof el.scrollTo !== 'function') el.scrollTop = Math.max(0, top);
    else el.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    first.current = false;
  }, [index, rowHeight]);

  const move = (to) => {
    const next = (to + items.length) % items.length;
    onSelect(items[next], true);
  };

  const onKeyDown = (e) => {
    const at = index < 0 ? 0 : index;
    const keys = {
      ArrowDown: () => move(index < 0 ? 0 : at + 1),
      ArrowUp: () => move(index < 0 ? 0 : at - 1),
      PageDown: () => move(Math.min(items.length - 1, at + 4)),
      PageUp: () => move(Math.max(0, at - 4)),
      Home: () => move(0),
      End: () => move(items.length - 1),
      ArrowRight: () => onNeighbour(1),
      ArrowLeft: () => onNeighbour(-1),
      Enter: () => onEnter?.(),
    };
    if (keys[e.key]) { e.preventDefault(); e.stopPropagation(); keys[e.key](); }
  };

  return (
    <div
      ref={listRef}
      role="listbox"
      tabIndex={0}
      aria-label={label}
      aria-activedescendant={index >= 0 ? `${baseId}-${index}` : undefined}
      onKeyDown={onKeyDown}
      style={{
        width: 64, height: rowHeight * VISIBLE_ROWS, overflowY: 'auto', scrollbarWidth: 'none',
        scrollSnapType: 'y proximity', borderRadius: 10, background: C.bg, outline: 'none',
        paddingBlock: 2, boxSizing: 'border-box',
      }}
      onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${C.navy}`; }}
      onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      {items.map((item, i) => {
        const active = i === index;
        return (
          <div
            key={String(item)}
            id={`${baseId}-${i}`}
            role="option"
            aria-selected={active}
            onClick={() => onSelect(item, false)}
            style={{
              height: rowHeight - 4, margin: '2px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 8, cursor: 'pointer', scrollSnapAlign: 'center', userSelect: 'none',
              fontSize: rowHeight > ROW ? 16 : 14, fontVariantNumeric: 'tabular-nums',
              fontWeight: active ? 700 : 500,
              background: active ? C.navy : 'transparent',
              color: active ? '#fff' : C.textPrimary,
              transition: 'background .12s, color .12s',
            }}
          >
            {render(item)}
          </div>
        );
      })}
    </div>
  );
}
